import React from "react";

import PropTypes from "prop-types";

import { ATTACHMENT_TYPES } from "manager/config/constants";
import Settings from "shared/components/Settings";
import Skeleton from "shared/components/Skeleton";

import ApplicationSetupHeader from "../Header";
import { useAttachmentValidation } from "../DealSetupForm/AttachmentValidationContext";
import { ConsolidatedAttachmentGrid } from "../ConsolidatedAttachmentGrid";
import {
  buildApplicantTypes,
  buildItemDisplayLabel,
  getAttachmentsFilter,
  mapFinancialDocLabels,
  useAttachmentHandlers,
  useDocQuantitiesUiEnabled,
} from "../utils";
import RequiredAttachmentsBackgroundSetupLegacy from "./RequiredAttachmentsBackgroundSetupLegacy";

const APPLICANT_TYPES_CONFIG = buildApplicantTypes({
  applicant: "requiresApplicantAdditionalAttachementsForBackground",
  occupant: "requiresOccupantAdditionalAttachementsForBackground",
  guarantor: "requiresGuarantorAdditionalAttachementsForBackground",
});

const ATTACHMENT_TYPE = ATTACHMENT_TYPES.UNVERIFIED_BACKGROUND;
const getRequiredAttachments = getAttachmentsFilter(ATTACHMENT_TYPE);

const RequiredAttachmentsBackgroundSetup = ({ loading, disabled, forSettingsLabel }) => {
  const uiEnabled = useDocQuantitiesUiEnabled();
  if (!uiEnabled) {
    return (
      <RequiredAttachmentsBackgroundSetupLegacy
        loading={loading}
        disabled={disabled}
        forSettingsLabel={forSettingsLabel}
      />
    );
  }

  return (
    <RequiredAttachmentsBackgroundSetupGrid
      loading={loading}
      disabled={disabled}
    />
  );
};

const RequiredAttachmentsBackgroundSetupGrid = ({ loading, disabled }) => {
  const { reportWarnings } = useAttachmentValidation();
  const {
    values,
    setFieldValue,
    onChangeAttachment,
    onChangeQuantity,
    handleAddCustomDoc,
    handleDeleteDoc,
  } = useAttachmentHandlers({
    attachmentType: ATTACHMENT_TYPE,
    personas: APPLICANT_TYPES_CONFIG,
  });

  if (values?.veroGoActive) {
    return null;
  }

  const allItems = APPLICANT_TYPES_CONFIG.flatMap((at) =>
    mapFinancialDocLabels(getRequiredAttachments(values, at.applicantType))
  );

  return (
    <div id="background-grid">
      <Settings
        header={
          <Skeleton loading={loading}>
            <ApplicationSetupHeader
              title="Unverified Credit & Background Setup"
              subTitle="Please manage the attachments that you want to require from applicants, occupants and guarantors in case their credit & background are not verified"
            />
          </Skeleton>
        }
        nodes={[
          {
            key: 1,
            component: (
              <Skeleton loading={loading}>
                <ConsolidatedAttachmentGrid
                  personas={APPLICANT_TYPES_CONFIG}
                  items={allItems}
                  values={values}
                  onTogglePersona={(field, value) =>
                    setFieldValue(field, value)
                  }
                  onToggleItem={onChangeAttachment}
                  onChangeQuantity={onChangeQuantity}
                  onAddCustomDoc={handleAddCustomDoc}
                  onDeleteDoc={handleDeleteDoc}
                  buildLabel={buildItemDisplayLabel}
                  sectionTitle="Unverified Credit & Background"
                  sectionId="background-grid"
                  onValidationChange={(w) => reportWarnings("background", w)}
                  disabled={disabled}
                />
              </Skeleton>
            ),
          },
        ]}
      />
    </div>
  );
};

RequiredAttachmentsBackgroundSetup.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  forSettingsLabel: PropTypes.string,
};

RequiredAttachmentsBackgroundSetup.defaultProps = {
  loading: false,
  disabled: false,
  forSettingsLabel: "Settings",
};

export default RequiredAttachmentsBackgroundSetup;
